"use client";

/**
 * LandingPagePublicWrapper
 * Client-side wrapper for public landing pages, organizer affordances, and password gating.
 */

import { LoadingPage } from "@/components/ui/loading-components/LoadingPage";
import { useUser } from "@/hooks/useUser";
import { getAccessType } from "@/lib/database/client/database";
import { LandingPage } from "@/lib/database/landing-pages";
import { AccessType } from "@/lib/users/types";
import { createClient } from "@/utils/supabase/client";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { FaPencilAlt } from "react-icons/fa";
import { verifyPassword } from "@/lib/utils/password";
import { buildLandingPageManagePath } from "@/lib/routes/landing-page-manage-paths";
import LandingPagePublicView from "./LandingPagePublicView";
import PasswordProtectionForm from "./PasswordProtectionForm";

interface LandingPagePublicWrapperProps {
  eventId: number;
  slug: string;
  initialLandingPage?: LandingPage | null;
  pageContext?: string; // "main" or subpage slug
}

export default function LandingPagePublicWrapper({
  eventId,
  slug,
  initialLandingPage,
  pageContext = "main",
}: LandingPagePublicWrapperProps) {
  // Hooks
  const supabase = createClient();
  const router = useRouter();
  const { user, isNowadaysAdmin } = useUser();
  const isNotFound = initialLandingPage === null;

  // States
  const [landingPage, setLandingPage] = useState<LandingPage | null>(initialLandingPage || null);
  const [isLoading, setIsLoading] = useState(initialLandingPage === undefined);
  const [isOrganizer, setIsOrganizer] = useState<boolean>(false);
  const [needsPassword, setNeedsPassword] = useState<boolean>(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [isVerifyingPassword, setIsVerifyingPassword] = useState<boolean>(false);

  // Effects
  useEffect(() => {
    const checkAccess = async () => {
      if (!landingPage) {
        return;
      }

      if (isNaN(eventId)) {
        router.push("/404");
        return;
      }

      // Check if user is organizer
      const accessType = await getAccessType(supabase, user?.user_id || 0, eventId);
      const userIsOrganizer = accessType === AccessType.OWNER || accessType === AccessType.ADMIN;
      setIsOrganizer(userIsOrganizer);

      // Check for password protection (organizers and admins bypass password)
      // For subpages, check the subpage-specific password
      let requiredPassword: string | undefined = undefined;
      if (pageContext !== "main" && landingPage.subpages) {
        const subpage = landingPage.subpages.find((s) => s.slug === pageContext);
        requiredPassword = subpage?.password;
      } else {
        requiredPassword = landingPage.password;
      }

      if (requiredPassword && !userIsOrganizer && !isNowadaysAdmin) {
        // Check if password was already verified in session storage
        const sessionKey = `landing_page_password_${landingPage.landing_page_id}_${pageContext}`;
        const storedEntry = sessionStorage.getItem(sessionKey);
        const isVerified = storedEntry && (await verifyPassword(storedEntry, requiredPassword));
        if (!isVerified) {
          setNeedsPassword(true);
        }
      }

      setIsLoading(false);
    };

    if (landingPage) {
      checkAccess();
    }
  }, [supabase, eventId, landingPage, router, user, isNowadaysAdmin, pageContext]);

  // Render
  if (isLoading) {
    return <LoadingPage />;
  }

  if (isNotFound) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-md p-8 max-w-md">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Page Not Found</h1>
          <p className="text-gray-600">The landing page you're looking for doesn't exist or couldn't be loaded.</p>
        </div>
      </div>
    );
  }

  if (!landingPage) {
    return null;
  }

  // Handlers
  const handleEditPage = () => {
    if (user) {
      window.open(buildLandingPageManagePath(slug), "_blank");
    }
  };

  const handlePasswordSubmit = async (enteredPassword: string) => {
    setIsVerifyingPassword(true);
    setPasswordError(null);

    // Get the required password for current page context
    let requiredPassword: string | undefined = undefined;
    if (pageContext !== "main" && landingPage.subpages) {
      const subpage = landingPage.subpages.find((s) => s.slug === pageContext);
      requiredPassword = subpage?.password;
    } else {
      requiredPassword = landingPage.password;
    }

    // Check if password matches (supports both hashed and plaintext)
    const matches = requiredPassword ? await verifyPassword(enteredPassword, requiredPassword) : false;
    if (matches) {
      // Store plaintext in session storage so session check can re-verify
      const sessionKey = `landing_page_password_${landingPage.landing_page_id}_${pageContext}`;
      sessionStorage.setItem(sessionKey, enteredPassword);
      setNeedsPassword(false);
    } else {
      setPasswordError("Incorrect password. Please try again.");
    }

    setIsVerifyingPassword(false);
  };

  // Show password form if needed
  if (needsPassword) {
    return (
      <PasswordProtectionForm onSubmit={handlePasswordSubmit} error={passwordError} isLoading={isVerifyingPassword} />
    );
  }

  return (
    <>
      {(isOrganizer || isNowadaysAdmin) && (
        <div className="fixed bottom-4 left-4 z-50">
          <button
            className="btn text-light-custom flex items-center gap-2 bg-dark-custom outline-none border-none shadow-lg"
            onClick={handleEditPage}
          >
            <FaPencilAlt />
            <span className="hidden sm:inline">Edit Landing Page</span>
          </button>
        </div>
      )}
      <LandingPagePublicView landingPage={landingPage} eventId={eventId} pageContext={pageContext} />
    </>
  );
}
